<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading
  from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const props = defineProps<{ groupIdx: number , stoneLayout: string}>();
const emit = defineEmits(["patch"]);

const store = useStore();
const {t} = useI18n();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3StonesForm.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, the variant is fetched per stone group via
 * getItemVariants(itemIdx, "stones.<groupIdx>.stone_addon") instead of a fixed idx.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone addon - stones.<groupIdx>.stone_addon
 */

const stoneAddon = ref({
  "stone_addon": {}
} as any)

const getStoneAddonData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneAddon.value.stone_addon = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_addon")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneAddonData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneAddonData(props.groupIdx)
}, {deep: true})

/**
 * addon variants (transferred from itemV3StonesForm.vue's "form variants")
 */

const active = computed(() => stoneAddon.value.stone_addon[0])

const variantsList = computed(() => active.value?.values || [])

// the current match should always be listed first, so it is always part of the
// directly visible values instead of potentially being hidden behind "more"
const orderedVariantsList = computed(() => {
  // do not show matched addon at first position

  // filter list by comparing props.stoneLayout with variantData.stone_laypout
  const filteredVariantsList = variantsList.value.filter((variantData: any) => variantData.stone_layout === props.stoneLayout)

  return filteredVariantsList
  /**
   * Old variant ordered list, first variant is always first position

   const matchedIdx = variantsList.value.findIndex((v: any) => v.match)
   if (matchedIdx <= 0) {
   return variantsList.value
   }
   const matchedValue = variantsList.value[matchedIdx]
   return [
   matchedValue,
   ...variantsList.value.slice(0, matchedIdx),
   ...variantsList.value.slice(matchedIdx + 1)
   ]
   */
})

// focus mode removed: all values are shown directly, the first 5 right away and the
// rest hidden behind the "more"/"less" toggle below
const visibleValues = computed(() => orderedVariantsList.value.slice(0, 20))

const moreValues = computed(() => orderedVariantsList.value.slice(20))

const hasMoreValues = computed(() => moreValues.value.length > 0)

// whether the "more" values (list entry with the right-arrow) have been revealed inline
const showMore = ref(false)

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

const newIcons = [
  "c391",
  "c281",
  "c66",
  "c31",
  "c22",
  "c21",
  "c18",
  "c17",
  "c5"
]

</script>

<template>
  <div v-if="active" class="w-full">
    <ul class="flex gap-x-1 flex-wrap mb-2 w-full w-full justify-center ">
      <!-- stone addon list - visible values (first 5) -->
      <template v-for="(variantData, variantIdx) in visibleValues">
        <li v-if="variantData.idx !== ''" :key="variantIdx"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
            @click="handleValuePatch(active.idx, variantData)"
            :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
        >
          <div
              class="h-12 w-15 rounded-2xl bg-white overflow-hidden relative flex items-center justify-center"
          >
            <img
                :src="newIcons.includes(variantData.idx.toLowerCase())
                  ? `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/400x300/${variantData.idx.toLowerCase()}.webp`
                  : `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/300x300/addon_${variantData.idx.toLowerCase()}.webp`"
                :srcset="newIcons.includes(variantData.idx.toLowerCase())
                  ? `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/800x600/${variantData.idx.toLowerCase()}.webp`
                  : `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/600x600/addon_${variantData.idx.toLowerCase()}.webp`"
                class="w-full h-full object-cover relative"
            >
          </div>
          <span
              class="text-parameter-text text-[10px] overflow-hidden max-w-15 mx-auto text-center mt-1">
              {{ variantData.title }}
            </span>
        </li>
      </template>
      <template v-for="(variantData, variantIdx) in visibleValues">
        <li v-if="variantData.idx === '' && !variantData.match" :key="variantIdx"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
            @click="handleValuePatch(active.idx, variantData)"
        >
          <div class="h-12 w-15 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
            <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 text-[#555956]">
              <path d="m2.5 2.5 7 7m0-7-7 7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <span class="text-parameter-text text-[10px] overflow-hidden max-w-15 mx-auto text-center mt-1">
          {{ t('components.parameters.stones.addons.no_addon') }}
        </span>
        </li>
      </template>
      <!-- "more" trigger - placed as the last entry of the focus list, looks like another list item -->
      <li v-if="hasMoreValues && !showMore"
          class="cursor-pointer flex flex-col items-center p-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = true"
      >
        <div class="h-12 w-15 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 -rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-15 mx-auto text-center mt-1">
          Mehr
        </span>
      </li>
      <!-- stone addon list - remaining values (revealed inline once the "more" entry above was clicked) -->
      <template v-if="showMore">
        <li v-for="(variantData, variantIdx) in moreValues" :key="'more-' + variantIdx"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
            @click="handleValuePatch(active.idx, variantData)"
            :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
        >
          <div
              class="h-12 w-15 rounded-2xl bg-white overflow-hidden relative flex items-center justify-center"
          >
            <img
                :src="newIcons.includes(variantData.idx.toLowerCase())
                  ? `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/400x300/${variantData.idx.toLowerCase()}.webp`
                  : `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/300x300/addon_${variantData.idx.toLowerCase()}.webp`"
                :srcset="newIcons.includes(variantData.idx.toLowerCase())
                  ? `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/800x600/${variantData.idx.toLowerCase()}.webp`
                  : `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/600x600/addon_${variantData.idx.toLowerCase()}.webp`"
                class="w-full h-full object-cover relative"
            >
          </div>
          <span
              class="text-parameter-text text-[10px] overflow-hidden max-w-15 mx-auto text-center mt-1">
              {{ variantData.title }}
            </span>

        </li>
      </template>
      <!-- "less" trigger - placed as the last entry once "more" is revealed, collapses the list again -->
      <li v-if="showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = false"
      >
        <div class="h-12 w-15 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-15 mx-auto text-center mt-1">
          Weniger
        </span>
      </li>
    </ul>
  </div>
  <item-v3-stones-parameter-loading v-else/>
</template>

<style scoped>
</style>
